import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, LogIn, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not sign in. Check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#011617] p-6">
      <div className="w-full max-w-md">
        <div className="bg-[#011617] rounded-2xl p-6 mb-6 flex justify-center">
          <img
            src="/netmark-logo.png"
            alt="NetMark Pro"
            className="h-20 w-auto object-contain"
            data-testid="login-logo"
          />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[#0F4F51]">
          <div className="mb-6">
            <div className="np-label mb-2 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" /> Admin sign-in
            </div>
            <h1 className="text-2xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
              Monthly Tasks
            </h1>
            <p className="text-sm text-[#595959] mt-1">
              This app is private. Sign in with your authorised email.
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="np-label block mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@netmarkpro.co.za"
                className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] focus:ring-1 focus:ring-[#00ACB3] outline-none bg-white"
                required
              />
            </div>
            <div>
              <label className="np-label block mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] focus:ring-1 focus:ring-[#00ACB3] outline-none bg-white"
                required
              />
            </div>
            {error && (
              <div
                data-testid="login-error"
                className="flex items-start gap-2 p-3 rounded-lg bg-[#FEE2E2] border border-[#F87171] text-sm text-[#991B1B]"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={busy || !email || !password}
              className="w-full bg-[#00ACB3] hover:bg-[#0F4F51] disabled:opacity-50 text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center justify-center gap-2 transition-colors"
            >
              <LogIn className="w-4 h-4" />
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>
          <p className="mt-6 text-xs text-[#595959] text-center">
            Forgot your password? Contact an admin to have it reset.
          </p>
        </div>
      </div>
    </div>
  );
}
